import { seriesColor } from '@/components/charts/palette';
import { formatShare } from '@/lib/format';

export interface BarListItem {
  key: string;
  label: string;
  /** The exact figure from the API. */
  display: string;
  /** Parsed value — bar geometry and the share percentage only. */
  value: number;
  /** Secondary line, e.g. "34 sold · 12 orders". */
  meta?: string;
}

export interface BarListProps {
  items: readonly BarListItem[];
  /** Accessible name for the list, e.g. "Sales by payment method". */
  label: string;
  /** Colour every bar the same (default) or by series index. */
  colorful?: boolean;
}

/**
 * Ranked horizontal bars. Plain HTML rather than SVG — the browser handles the
 * text wrapping and it stays readable at any width.
 */
/** Single-series bars use the brand navy (`brand-700`). */
const BAR_COLOR = '#0F2E50';

export function BarList({ items, label, colorful = false }: BarListProps) {
  const max = Math.max(0, ...items.map((item) => item.value));
  const total = items.reduce((sum, item) => sum + item.value, 0);

  return (
    <ul aria-label={label} className="space-y-3">
      {items.map((item, index) => {
        const ratio = max > 0 ? Math.max(item.value / max, item.value > 0 ? 0.015 : 0) : 0;
        return (
          <li key={item.key}>
            <div className="flex items-baseline justify-between gap-3">
              <p className="min-w-0 truncate text-sm font-medium text-slate-700" title={item.label}>
                {item.label}
              </p>
              <p className="shrink-0 text-sm font-semibold tabular-nums text-slate-900">{item.display}</p>
            </div>
            <div className="mt-1.5 flex items-center gap-3">
              <div aria-hidden="true" className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(ratio * 100).toFixed(2)}%`,
                    // Was a hardcoded #4f46e5 — indigo from a theme two changes
                    // ago, which survived both the green and the navy retheme
                    // because it never went through a token.
                    backgroundColor: colorful ? seriesColor(index) : BAR_COLOR,
                  }}
                />
              </div>
              <p className="w-10 shrink-0 text-right text-xs tabular-nums text-slate-400">
                {formatShare(item.value, total)}
              </p>
            </div>
            {item.meta ? <p className="mt-0.5 text-xs text-slate-400">{item.meta}</p> : null}
          </li>
        );
      })}
    </ul>
  );
}
